import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Link2, CheckCircle2, AlertTriangle, Search } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { api } from "../lib/api";
import { Money } from "./SaudiRiyal";
import { FuelSmartMatch } from "./FuelSmartMatch";
import { FuelFleetTools } from "./FuelFleetTools";
import { personMatch } from "../lib/searchText";

export const FuelPlateMatchDialog = ({ open, onOpenChange, month, provider, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingPlate, setSavingPlate] = useState(null);
  const [q, setQ] = useState("");
  const [onlyUnmatched, setOnlyUnmatched] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.fuelPlateMatches(month || undefined, provider || undefined)); }
    catch { toast.error("تعذّر تحميل مطابقة اللوحات"); }
    finally { setLoading(false); }
  }, [month, provider]);

  useEffect(() => { if (open) { setQ(""); load(); } }, [open, load]);

  const assign = async (plate, employee_id) => {
    setSavingPlate(plate);
    try {
      const r = await api.fuelPlateMap(plate, employee_id);
      if (employee_id && !r?.updated) toast.warning("حُفظ الربط للاستيرادات القادمة، لكن لم يُعثر على سجلات وقود بهذه اللوحة حالياً");
      else toast.success(employee_id ? `تم ربط اللوحة (${r?.updated ?? 0} سجل)` : "تم إلغاء الربط");
      await load();
      onChanged?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "تعذّر الحفظ"); }
    finally { setSavingPlate(null); }
  };

  const vehicles = data?.vehicles || [];
  const plates = (data?.plates || []).filter((p) => {
    if (onlyUnmatched && p.matched) return false;
    if (!q.trim()) return true;
    const s = q.trim().toLowerCase();
    return (p.plate || "").toLowerCase().includes(s) || personMatch(p, s);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" dir="rtl" data-testid="fuel-plate-match-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Link2 className="h-5 w-5 text-emerald-600" /> مطابقة لوحات الوقود مع الأسطول
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center justify-between gap-2 pb-2">
          <div className="flex items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-1" data-testid="fuel-match-matched-count">
              <CheckCircle2 className="h-3.5 w-3.5" /> مطابقة: {data?.matched ?? 0}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 px-2.5 py-1" data-testid="fuel-match-unmatched-count">
              <AlertTriangle className="h-3.5 w-3.5" /> غير مطابقة: {data?.unmatched ?? 0}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setOnlyUnmatched((v) => !v)} data-testid="fuel-match-filter-unmatched"
              className={`text-xs rounded-md border px-2.5 py-1.5 ${onlyUnmatched ? "border-amber-300 bg-amber-50 text-amber-700" : "border-slate-200 bg-white text-slate-500"}`}>
              غير المطابقة فقط
            </button>
            <div className="relative">
              <Search className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <Input data-testid="fuel-match-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث بلوحة أو اسم" className="h-8 w-44 pr-8 text-sm" />
            </div>
          </div>
        </div>

        <div className="pb-2">
          <FuelSmartMatch month={month} provider={provider} vehicles={vehicles} onDone={() => { load(); onChanged?.(); }} />
          <FuelFleetTools onChanged={() => { load(); onChanged?.(); }} />
        </div>

        <div className="overflow-y-auto flex-1 -mx-1 px-1">
          {loading ? (
            <div className="py-16 text-center text-slate-300"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
          ) : plates.length === 0 ? (
            <div className="py-16 text-center text-slate-400 text-sm">لا توجد لوحات {onlyUnmatched ? "غير مطابقة" : ""}</div>
          ) : (
            <div className="space-y-1.5">
              {plates.map((p) => (
                <div key={p.plate} data-testid={`fuel-plate-row-${p.plate}`}
                  className={`grid grid-cols-[1.1fr_0.8fr_0.9fr_1.6fr] items-center gap-2 rounded-xl border px-3 py-2 ${p.matched ? "border-emerald-100 bg-emerald-50/40" : "border-amber-200 bg-amber-50/50"}`}>
                  <div className="min-w-0">
                    <span className="font-mono-nums font-bold text-slate-800" dir="ltr">{p.plate}</span>
                    {p.matched ? (
                      <div className="text-[11px] text-emerald-700 truncate">{p.employee_name}</div>
                    ) : (
                      <div className="text-[11px] text-amber-600">بحاجة لربط</div>
                    )}
                  </div>
                  <div className="text-center text-xs text-slate-500 font-mono-nums" title={p.liters_estimated ? "الملف بدون لترات — تقدير من المبلغ ÷ سعر اللتر" : ""}>{p.liters_estimated ? "≈ " : ""}{p.liters} ل</div>
                  <div className="text-center text-xs font-mono-nums font-semibold text-emerald-700"><Money value={p.total} /></div>
                  <div className="flex items-center gap-1.5 justify-end">
                    <select data-testid={`fuel-plate-select-${p.plate}`} disabled={savingPlate === p.plate}
                      value={p.employee_id || ""} onChange={(e) => assign(p.plate, e.target.value)}
                      className="h-8 w-full max-w-[220px] rounded-md border border-slate-200 bg-white px-2 text-sm">
                      <option value="">— بدون ربط —</option>
                      {vehicles.map((v) => (
                        <option key={v.employee_id} value={v.employee_id}>
                          {v.name}{v.vehicle_number ? ` — ${v.vehicle_number}` : ""}{v.branch ? ` (${v.branch})` : ""}
                        </option>
                      ))}
                    </select>
                    {savingPlate === p.plate && <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <p className="text-[11px] text-slate-400 pt-1">تُحفظ الربطة وتُطبَّق تلقائياً على الاستيرادات القادمة لنفس اللوحة.</p>
      </DialogContent>
    </Dialog>
  );
};
